import React from 'react';
import { AlertTriangle, MessageCircle, Phone, X } from 'lucide-react';
import {
  SUPPORT_MOBILE,
  buildWhatsAppUrl,
  dismissLicenseWarn,
  telSupportHref,
} from '../../services/managementService';

function formatDate(value) {
  if (!value) return '—';
  return String(value).slice(0, 10);
}

/**
 * Soft warning modal when license is expired (app remains usable).
 */
export default function LicenseExpiredWarning({ portal, onClose }) {
  if (!portal) return null;

  const expired = (portal.Licenses || []).filter((l) => l.IsExpired);
  const latest = expired[0] || portal.Licenses?.[0];

  const waUrl = buildWhatsAppUrl({
    customerName: portal.CustomerName,
    registerCode: portal.Code,
    reason: 'Our software license has expired and we need to renew.',
    details: latest
      ? `License key: ${latest.LicenseKey || '—'}; Expiry: ${formatDate(latest.ExpiryDate)}`
      : undefined,
  });

  const handleClose = () => {
    dismissLicenseWarn();
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/50">
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-amber-200 overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="license-expired-title"
      >
        <div className="bg-amber-50 px-5 py-4 border-b border-amber-100 flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h2 id="license-expired-title" className="text-lg font-bold text-amber-950">
              License expired
            </h2>
            <p className="text-sm text-amber-900/80 mt-1">
              Your license term has ended. You can continue using the software for now, but please
              renew soon to avoid interruption.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg p-1.5 text-amber-800 hover:bg-amber-100"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3 text-sm">
          {latest ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-slate-500 text-xs uppercase font-semibold tracking-wide">
                Current license
              </p>
              <p className="font-semibold text-slate-900 mt-1 break-all">{latest.LicenseKey || '—'}</p>
              <p className="text-slate-600 mt-1">
                Expired on {formatDate(latest.ExpiryDate)}
                {latest.ContractNo ? ` · ${latest.ContractNo}` : ''}
              </p>
            </div>
          ) : (
            <p className="text-slate-600">No active license was found for this register code.</p>
          )}

          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp to renew
            </a>
            <a
              href={telSupportHref()}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white"
            >
              <Phone className="w-4 h-4" />
              {SUPPORT_MOBILE}
            </a>
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Continue for now
          </button>
        </div>
      </div>
    </div>
  );
}
